/**
 * עובדים והרשאות — members, roles and invitations.
 *
 * The invitation token is shown exactly once, in the response that creates it, because only
 * its hash is stored. So the screen has to make that clear and make copying easy: an
 * invitation that scrolls away is an invitation that has to be reissued.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { formatDate, formatDateTime, type OrgRole } from "@onecpa/shared";
import {
  Badge,
  Button,
  ConfirmDialog,
  Dialog,
  EmptyState,
  ErrorState,
  Field,
  Input,
  KpiStrip,
  PageHeader,
  Panel,
  Select,
  TabStrip,
} from "@onecpa/ui";
import { api } from "../api/client.js";
import { useRoleGate } from "../app/hooks.js";
import { ROLE_LABELS } from "../app/navigation.js";
import { useSession } from "../app/session.js";

export function Members() {
  const session = useSession();
  const queryClient = useQueryClient();
  const { allowed } = useRoleGate(["owner", "manager"]);
  const isOwner = session.role === "owner";

  const [tab, setTab] = useState<"members" | "invitations">("members");
  const [inviting, setInviting] = useState(false);
  const [removing, setRemoving] = useState<{ id: string; name: string } | null>(null);

  const members = useQuery({
    queryKey: ["members", session.orgId],
    queryFn: () => api.settings.members(session.orgId),
    enabled: allowed,
  });

  const invitations = useQuery({
    queryKey: ["invitations", session.orgId],
    queryFn: () => api.settings.invitations(session.orgId),
    enabled: allowed,
  });

  const organization = useQuery({
    queryKey: ["organization", session.orgId],
    queryFn: () => api.settings.organization(session.orgId),
    enabled: allowed,
  });

  const changeRole = useMutation({
    mutationFn: (input: { id: string; role: OrgRole }) =>
      api.settings.updateMember(session.orgId, input.id, input.role),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["members"] });
      session.toast("ההרשאה עודכנה");
    },
    onError: (error: Error) => session.toast(error.message, "danger"),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.settings.removeMember(session.orgId, id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["members"] });
      session.toast("המשתמש הוסר מהמשרד");
      setRemoving(null);
    },
    onError: (error: Error) => session.toast(error.message, "danger"),
  });

  const revoke = useMutation({
    mutationFn: (id: string) => api.settings.revokeInvitation(session.orgId, id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["invitations"] });
      session.toast("ההזמנה בוטלה");
    },
    onError: (error: Error) => session.toast(error.message, "danger"),
  });

  if (!allowed) {
    return (
      <ErrorState
        title="אין הרשאה למסך זה"
        message="ניהול עובדים והרשאות זמין לבעל המשרד ולמנהל המשרד."
      />
    );
  }

  const rows = members.data?.members ?? [];
  const seats = rows.filter((row) => row.role !== "client_user").length;
  const maxSeats = organization.data?.org.maxSeats;

  return (
    <>
      <PageHeader
        title="עובדים והרשאות"
        subtitle={session.orgName}
        action={
          <Button variant="primary" icon="plus" onClick={() => setInviting(true)}>
            הזמנת משתמש
          </Button>
        }
      >
        <TabStrip
          tabs={[
            { id: "members", label: "משתמשים", count: rows.length },
            {
              id: "invitations",
              label: "הזמנות",
              count: (invitations.data?.invitations ?? []).filter((row) => row.state === "pending")
                .length,
            },
          ]}
          active={tab}
          onSelect={(id) => setTab(id as "members" | "invitations")}
        />
      </PageHeader>

      <KpiStrip
        loading={members.isLoading}
        items={[
          {
            label: "מושבים בשימוש",
            value: maxSeats ? `${seats}/${maxSeats}` : seats,
            tone: "ledger",
          },
          { label: "בעלי משרד", value: rows.filter((row) => row.role === "owner").length },
          { label: "מנהלי חשבונות", value: rows.filter((row) => row.role === "staff").length },
          {
            label: "לקוחות בפורטל",
            value: rows.filter((row) => row.role === "client_user").length,
          },
          {
            label: "עם אימות דו-שלבי",
            value: rows.filter((row) => row.mfaEnrolled).length,
            tone: rows.some((row) => !row.mfaEnrolled) ? "ochre" : "ledger",
          },
        ]}
      />

      <div className="p-5">
        {tab === "members" && (
          <Panel title="משתמשי המשרד">
            {rows.length > 0 ? (
              <ul className="divide-y divide-line-soft">
                {rows.map((member) => (
                  <li key={member.id} className="flex flex-wrap items-center gap-3 px-3.5 py-2.5">
                    <span className="grid size-7 shrink-0 place-items-center rounded-full bg-primary-soft text-xs font-bold text-ledger">
                      {member.fullName.charAt(0)}
                    </span>
                    <span className="min-w-40 flex-1">
                      <span className="block truncate text-sm font-semibold text-ink">
                        {member.fullName}
                      </span>
                      <bdi className="block truncate text-2xs text-ink-faint" dir="ltr">
                        {member.email}
                      </bdi>
                    </span>

                    {member.mfaEnrolled ? (
                      <Badge tone="success">2FA</Badge>
                    ) : (
                      <Badge tone="warning">אין 2FA</Badge>
                    )}

                    {member.lastSeenAt && (
                      <bdi className="tnum hidden text-2xs text-ink-faint md:block">
                        {formatDateTime(member.lastSeenAt)}
                      </bdi>
                    )}

                    {isOwner && member.userId !== session.me.user.id ? (
                      <Select
                        value={member.role}
                        aria-label={`הרשאה של ${member.fullName}`}
                        className="w-36"
                        onChange={(event) =>
                          changeRole.mutate({ id: member.id, role: event.target.value as OrgRole })
                        }
                      >
                        {Object.entries(ROLE_LABELS).map(([value, label]) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                      </Select>
                    ) : (
                      <Badge tone="neutral">{ROLE_LABELS[member.role] ?? member.role}</Badge>
                    )}

                    {isOwner && member.userId !== session.me.user.id && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setRemoving({ id: member.id, name: member.fullName })}
                      >
                        הסרה
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <EmptyState title="אין משתמשים" />
            )}
          </Panel>
        )}

        {tab === "invitations" && (
          <Panel title="הזמנות">
            {(invitations.data?.invitations ?? []).length > 0 ? (
              <ul className="divide-y divide-line-soft">
                {(invitations.data?.invitations ?? []).map((invitation) => (
                  <li
                    key={invitation.id}
                    className="flex flex-wrap items-center gap-3 px-3.5 py-2.5"
                  >
                    <bdi className="min-w-40 flex-1 truncate text-xs text-ink" dir="ltr">
                      {invitation.email}
                    </bdi>
                    <Badge tone="neutral">{ROLE_LABELS[invitation.role] ?? invitation.role}</Badge>
                    <Badge
                      tone={
                        invitation.state === "pending"
                          ? "pending"
                          : invitation.state === "accepted"
                            ? "success"
                            : invitation.state === "expired"
                              ? "warning"
                              : "neutral"
                      }
                    >
                      {INVITATION_STATES[invitation.state] ?? invitation.state}
                    </Badge>
                    <bdi className="tnum text-2xs text-ink-faint">
                      {formatDate(invitation.expiresAt)}
                    </bdi>
                    {invitation.state === "pending" && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => revoke.mutate(invitation.id)}
                      >
                        ביטול
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <EmptyState title="אין הזמנות" icon="users" />
            )}
          </Panel>
        )}
      </div>

      <InviteDialog open={inviting} onClose={() => setInviting(false)} />

      <ConfirmDialog
        open={removing !== null}
        onClose={() => setRemoving(null)}
        onConfirm={() => removing && remove.mutate(removing.id)}
        title="הסרת משתמש"
        message={`${removing?.name ?? ""} יאבד גישה למשרד באופן מיידי. ההיסטוריה שלו נשמרת ביומן הביקורת.`}
        confirmLabel="הסרה"
        destructive
        loading={remove.isPending}
      />
    </>
  );
}

const INVITATION_STATES: Record<string, string> = {
  pending: "ממתינה",
  accepted: "נוצלה",
  expired: "פג תוקף",
  revoked: "בוטלה",
};

function InviteDialog({ open, onClose }: { open: boolean; onClose(): void }) {
  const session = useSession();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<OrgRole>("staff");
  const [clientId, setClientId] = useState("");
  const [issued, setIssued] = useState<{ token: string; email: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data: clients } = useQuery({
    queryKey: ["clients", session.orgId, "", "active"],
    queryFn: () => api.clients.list(session.orgId, { status: "active", limit: 200 }),
    enabled: open && role === "client_user",
  });

  const invite = useMutation({
    mutationFn: () =>
      api.settings.invite(session.orgId, {
        email: email.trim(),
        role,
        ...(role === "client_user" && clientId ? { clientId } : {}),
      }),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ["invitations"] });
      setIssued({ token: result.token, email: result.invitation.email });
      setEmail("");
    },
    onError: (mutationError: Error) => setError(mutationError.message),
  });

  const inviteUrl = issued ? `${window.location.origin}/?invite=${issued.token}` : "";

  return (
    <Dialog
      open={open}
      onClose={() => {
        setIssued(null);
        onClose();
      }}
      title="הזמנת משתמש"
      description="ההזמנה תקפה 7 ימים ומיועדת לכתובת הדוא״ל שהוזנה בלבד."
      footer={
        issued ? (
          <Button
            variant="primary"
            onClick={() => {
              setIssued(null);
              onClose();
            }}
          >
            סיום
          </Button>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose}>
              ביטול
            </Button>
            <Button
              variant="primary"
              disabled={email.trim().length === 0 || (role === "client_user" && !clientId)}
              loading={invite.isPending}
              onClick={() => {
                setError(null);
                invite.mutate();
              }}
            >
              יצירת הזמנה
            </Button>
          </>
        )
      }
    >
      {issued ? (
        <div className="flex flex-col gap-3">
          <p className="text-xs text-ink">
            הקישור נוצר עבור <bdi dir="ltr">{issued.email}</bdi>. הוא מוצג פעם אחת בלבד — במסד
            הנתונים נשמר רק גיבוב שלו, ולכן לא ניתן לשחזר אותו אחר כך.
          </p>
          <Input
            readOnly
            value={inviteUrl}
            dir="ltr"
            onFocus={(event) => event.currentTarget.select()}
          />
          <Button
            onClick={() => {
              void navigator.clipboard.writeText(inviteUrl);
              session.toast("הקישור הועתק");
            }}
          >
            העתקת הקישור
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-3.5">
          <Field label="כתובת דוא״ל" required>
            {({ id }) => (
              <Input
                id={id}
                type="email"
                dir="ltr"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            )}
          </Field>

          <Field label="הרשאה" help="בעל משרד רואה גם רווחיות ומנוי; מנהל חשבונות רק את לקוחותיו">
            {({ id }) => (
              <Select
                id={id}
                value={role}
                onChange={(event) => setRole(event.target.value as OrgRole)}
              >
                {Object.entries(ROLE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          {role === "client_user" && (
            <Field label="לקוח" required help="חשבון לקוח משויך ללקוח אחד בלבד">
              {({ id }) => (
                <Select
                  id={id}
                  value={clientId}
                  onChange={(event) => setClientId(event.target.value)}
                >
                  <option value="">בחרו לקוח</option>
                  {(clients?.clients ?? []).map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.name}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          )}

          {error && (
            <p role="alert" className="text-xs font-semibold text-red-ink">
              {error}
            </p>
          )}
        </div>
      )}
    </Dialog>
  );
}
